import { useState } from "react";
import { motion } from "framer-motion";
import { CONTACT } from "../data/content";
import { DEST_IMAGES, HERO_IMAGE } from "../data/images";

const CITIES = [
  "Indore",
  "Bhopal",
  "Ujjain",
  "Maheshwar",
  "Pachmarhi",
  "Omkareshwar",
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay: 0.15 + i * 0.12,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

function CityField({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-1 text-left">
      <label className="text-xs font-medium uppercase tracking-wide text-navy/50">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-xl border border-navy/10 bg-white px-3 py-3 text-sm font-medium outline-none focus:border-brand ${
          value ? "text-navy" : "text-navy/40"
        }`}
      >
        <option value="">Select city</option>
        {options.map((c) => (
          <option key={c} value={c} className="text-navy">
            {c}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function Hero() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  return (
    <section
      id="home"
      className="relative bg-gradient-to-br from-brand via-brand to-brand-dark pb-44 pt-32 text-white lg:pb-48"
    >
      {/* Decorative layer, clipped so it never overflows the section,
          while the search bar below stays free to overhang. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Watermark */}
        <span className="absolute -top-2 left-1/2 hidden -translate-x-1/2 select-none whitespace-nowrap font-heading text-[14vw] font-extrabold text-white/10 lg:block">
          JSR TRAVELS
        </span>

        {/* Decorative floating shapes */}
        <div className="absolute right-10 top-24 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute left-1/3 top-40 hidden animate-float text-4xl lg:block">
          🚗
        </div>
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
        {/* Left copy */}
        <div className="relative z-10">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-sm font-medium"
          >
            <span className="text-accent">★</span> Premium Chauffeur Services,
            Indore
          </motion.p>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-heading text-4xl font-extrabold leading-[1.08] sm:text-5xl lg:text-[56px]"
          >
            Book Your Next <br />
            <span className="text-accent">Journey</span> Today
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-5 max-w-md text-base text-white/85"
          >
            Clean cars, buses and travellers with professional chauffeurs
            for outstation trips, airport transfers and tours across Indore,
            Ujjain, Bhopal and all across India.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-8 flex flex-wrap gap-4"
          >
            <a href="#tours" className="btn-primary">
              Explore Tours →
            </a>
            <a
              href="https://www.instagram.com/toofan_travels.indore"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Watch our reels on Instagram"
              className="btn-outline"
            >
              <span className="grid h-7 w-7 place-items-center rounded-full bg-white/20">
                ▶
              </span>
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-8 flex flex-wrap items-center gap-6 text-sm text-white/80"
          >
            <span>⭐ 4.6/5 Rated</span>
            <span>🚗 6 Vehicle Types</span>
            <span>🕑 24/7 Support</span>
          </motion.div>
        </div>

        {/* Right illustration panel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10"
        >
          <div className="relative aspect-[4/3] overflow-hidden rounded-card bg-gradient-to-tr from-brand-light to-white/30 shadow-cardHover">
            <img
              src={HERO_IMAGE}
              alt="Half Dome and Yosemite Valley meadow at sunset"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
          </div>

          {/* Floating tour preview card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="absolute bottom-4 -left-4 w-60 rounded-card bg-white p-4 text-navy shadow-cardHover sm:-left-8"
          >
            <div className="flex items-center gap-3">
              <img
                src={DEST_IMAGES.ujjain.card}
                alt="Ujjain"
                className="h-14 w-14 rounded-xl object-cover"
              />
              <div>
                <p className="text-xs font-medium text-accent">Popular Trip</p>
                <p className="font-heading text-sm font-bold">Ujjain Darshan</p>
                <p className="text-xs text-navy/50">55 km · 1 hr</p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm font-bold">₹2,499</span>
              <span className="rounded-full bg-brand-tint px-3 py-1 text-xs font-semibold text-brand">
                Book →
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Search bar */}
      <motion.div
        id="hero-search"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="absolute inset-x-0 bottom-0 z-20 mx-auto w-full max-w-6xl translate-y-[65%] px-5 lg:px-8"
      >
        <div className="grid grid-cols-1 gap-4 rounded-card bg-white p-6 text-navy shadow-search ring-1 ring-navy/5 md:grid-cols-5 md:items-end md:gap-5">
          <CityField
            label="From"
            value={from}
            onChange={setFrom}
            options={CITIES.filter((c) => c !== to)}
          />
          <CityField
            label="To"
            value={to}
            onChange={setTo}
            options={CITIES.filter((c) => c !== from)}
          />
          <div className="flex flex-col gap-1 text-left">
            <label className="text-xs font-medium uppercase tracking-wide text-navy/50">
              Journey Date
            </label>
            <input
              type="date"
              className="w-full rounded-xl border border-navy/10 bg-white px-3 py-3 text-sm font-medium text-navy outline-none focus:border-brand"
            />
          </div>
          <div className="flex flex-col gap-1 text-left">
            <label className="text-xs font-medium uppercase tracking-wide text-navy/50">
              Return Date
            </label>
            <input
              type="date"
              className="w-full rounded-xl border border-navy/10 bg-white px-3 py-3 text-sm font-medium text-navy outline-none focus:border-brand"
            />
          </div>
          <a
            href={`https://wa.me/${CONTACT.wa}?text=Hi%20JSR%20Travels%2C%20I%20want%20to%20check%20availability`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-brand h-[46px] w-full"
          >
            Search
          </a>
        </div>
      </motion.div>
    </section>
  );
}
